import React, { useEffect } from 'react';
import './styles/MovementDetailModal.css';
import { formatFirebaseTimestamp } from '../../utils/datetime';

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
);

const DetailRow = ({ label, value }) => (
    <div className="md-detail-row">
        <span className="md-detail-label">{label}</span>
        <span className="md-detail-value">{value ?? '—'}</span>
    </div>
);

export default function MovementDetailModal({ movement, onClose }) {
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    if (!movement) return null;

    const displayName = movement.name || movement.title || movement.id;
    const initials = displayName
        ? displayName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
        : '#';

    return (
        <div className="md-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="md-modal" role="dialog" aria-modal="true">
                <button className="md-close" onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </button>

                <div className="md-header">
                    <div className="md-avatar">{initials}</div>
                    <div className="md-header-info">
                        <div className="md-name">{displayName}</div>
                        <div className="md-badges">
                            {movement.type && <span className="md-badge">{movement.type}</span>}
                            {movement.category && <span className="md-badge">{movement.category}</span>}
                        </div>
                    </div>
                </div>

                <div className="md-body">
                    <div className="md-details">
                        <DetailRow label="Movement ID" value={<span className="md-mono">{movement.id}</span>} />
                        {movement.createdAt && (
                            <DetailRow label="Created" value={formatFirebaseTimestamp(movement.createdAt)} />
                        )}
                        {movement.createdBy && (
                            <DetailRow label="Created by" value={<span className="md-mono">{movement.createdBy}</span>} />
                        )}
                        {movement.description && (
                            <DetailRow label="Description" value={movement.description} />
                        )}
                        {movement.location && (
                            <DetailRow label="Location" value={movement.location} />
                        )}
                        {movement.status != null && (
                            <DetailRow
                                label="Status"
                                value={
                                    <span className={`md-status ${movement.status === 'active' ? 'active' : 'inactive'}`}>
                                        {movement.status}
                                    </span>
                                }
                            />
                        )}
                        {movement.memberCount != null && (
                            <DetailRow label="Members" value={movement.memberCount} />
                        )}
                        {movement.updatedAt && (
                            <DetailRow label="Updated" value={formatFirebaseTimestamp(movement.updatedAt)} />
                        )}
                        {/* Render any remaining unknown fields */}
                        {Object.entries(movement)
                            .filter(([k]) => !['id','name','title','type','category','createdAt','createdBy',
                                'description','location','status','memberCount','updatedAt'].includes(k))
                            .map(([k, v]) => {
                                if (typeof v === 'object' && v !== null) return null;
                                return <DetailRow key={k} label={k} value={String(v)} />;
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
}
